// App.jsx is the root component for the application.
// It sets up routing, global providers, and initial socket/notification dispatch.
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet
} from "react-router-dom";
import "./styles/Themes.css";

// Import components for various routes
import Settings from "./Component/Settings";
import PostForm from "./Component/CreatePost";
import PollForm from "./Component/CreatePoll";
import PostPage from "./Component/PostPage";
import HomePage from "./Component/HomePage";
import Navbar from "./Component/Navbar";
import Friends from "./Component/Friends";
import Groups from "./Component/Groups";
import Modal from "./Component/Modal";
import PromiseComponent from "./Component/PromiseComponent";
import RegisterForm from "./Component/RegisterForm";
import ResetPassword from "./Component/ResetPassword";
import Messaging from "./Component/Messaging";
import { AboutMe } from "./Component/AboutMe";
import { Arafat } from "./Component/Arafat";
import { AboutMeDaShawn } from "./Component/AboutMeDaShawn";
import { Mdjim } from "./Component/Mdjim";
import { Pandahxb } from "./Component/Pandahxb";
import Profile from "./Component/Profile";
import Styleguide from "./Component/Styleguide.jsx";
import GameSelect from "./Component/GameSelect.jsx";
import GameCommunityPage from "./Component/GameCommunityPage.jsx";
import ProfileGameSelect from "./Component/ProfileGameSelect.jsx";
import Quiz from "./Component/Quiz.jsx";
import FilterSearch from "./Component/FilterSearch.jsx";

// Context provider for user attributes
import { UserProvider } from "./context/UserAttrContext";
// Socket.io client for real-time
import { io } from "socket.io-client";
import { NotificationsProvider } from "./context/NotificationsContext";
import { clearSession, getSessionAuth, isAuthenticated } from "./utils/auth";
import { applyTheme, getStoredDarkMode } from "./utils/theme";

// Initialize socket with path and tenantID
const socket = io(process.env.REACT_APP_API_PATH_SOCKET, {
  path: "/hci/api/realtime-socket/socket.io",
  query: { tenantID: "punchline" }
});
export { socket };

// Layout component wraps private routes with Navbar and renders child routes
// function Layout({ logout }) {
//   return (
//     <>
//       {/* Navbar displayed on all protected routes */}
//       <Navbar logout={logout} />
//       {/* Outlet renders nested route element */}
//       <Outlet />
//     </>
//   );
// }

function App() {
  // track login state
  const [loggedIn, setLoggedIn] = useState(false);
  // modal visibility
  const [openModal, setOpenModal] = useState(false);
  // trigger for refreshing posts
  const [refreshPosts, setRefreshPosts] = useState(false);

  const [checkingLogin, setCheckingLogin] = useState(true)
  // on mount, check session storage for a user
  useEffect(() => {
    applyTheme(getStoredDarkMode());
    if (isAuthenticated()) setLoggedIn(true);
    setCheckingLogin(false);
  }, []);
  // Layout: mounts dispatcher *and* NavBar together
  function Layout({ logout }) {
    const { userId, token } = getSessionAuth();

    return (
      <NotificationsProvider userId={userId} token={token}>
        <Navbar logout={logout} />
        <Outlet />
      </NotificationsProvider>
    );
  }

  // logout clears session and hides navbar
  const logout = e => {
    e?.preventDefault();
    clearSession();
    setLoggedIn(false);
  };

  // refresh trigger passed to HomePage
  const doRefreshPosts = () => setRefreshPosts(true);
  // toggle for modal
  const toggleModal = e => { e?.preventDefault(); setOpenModal(prev => !prev); };

  return (
    <UserProvider>
      <Router basename={process.env.PUBLIC_URL}>
        <div className="App">
          <header className="App-header">
            <div className="maincontent" id="mainContent">
              {!checkingLogin && (
                <Routes>
                  {/* Public routes - no navbar */}
                  <Route
                    path="/"
                    element={
                      loggedIn ? <Layout logout={logout} /> : (
                        <HomePage
                          setLoggedIn={setLoggedIn}
                          doRefreshPosts={doRefreshPosts}
                          appRefresh={refreshPosts}
                        />
                      )
                    }
                  >
                    {loggedIn && (
                      <Route
                        index
                        element={
                          <HomePage
                            setLoggedIn={setLoggedIn}
                            doRefreshPosts={doRefreshPosts}
                            appRefresh={refreshPosts}
                          />
                        }
                      />
                    )}
                  </Route>
                  <Route path="/register" element={<RegisterForm setLoggedIn={setLoggedIn} />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Protected routes - show navbar via Layout */}
                  <Route
                    element={loggedIn ? <Layout logout={logout} /> : <Navigate to="/" replace />}
                  >
                    <Route path="settings" element={<Settings />} />
                    <Route path="CreatePost" element={<PostForm />} />
                    <Route path="CreatePoll" element={<PollForm />} />
                    <Route path="post/:postId" element={<PostPage />} />
                    <Route path="friends" element={<Friends />} />
                    <Route path="groups" element={<Groups />} />
                    <Route path="promise" element={<PromiseComponent />} />
                    <Route path="messages/:roomID" element={<Messaging />} />
                    <Route path="aboutme-tauhidur" element={<AboutMe />} />
                    <Route path="Arafat" element={<Arafat />} />
                    <Route path="AboutMeDaShawn" element={<AboutMeDaShawn />} />
                    <Route path="Mdjim" element={<Mdjim />} />
                    <Route path="Pandahxb" element={<Pandahxb />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="profile/:userId" element={<Profile />} />
                    <Route path="Styleguide" element={<Styleguide />} />
                    <Route path="GameSelect" element={<GameSelect />} />
                    <Route path="ProfileGameSelect" element={<ProfileGameSelect />} />
                    <Route path="game/:gameName" element={<GameCommunityPage />} />
                    <Route path="quiz" element={<Quiz />} />
                    <Route path="filtersearch" element={<FilterSearch />} />
                  </Route>

                  {/* Catch-all redirects to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              )}
            </div>
          </header>
        </div>
        {/* Global modal component */}
        <Modal show={openModal} onClose={toggleModal}>
          This is a modal dialog!
        </Modal>
      </Router>
    </UserProvider>
  );
}

export default App;
